import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { useState } from "react";

interface ProfileHeaderProps {
  session: Session | null;
  avatarUrl: string | null;
  isUploading: boolean;
}

export function ProfileHeader({ session, avatarUrl, isUploading }: ProfileHeaderProps) {
  const { toast } = useToast();
  const [isLocalUploading, setIsLocalUploading] = useState(false);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !session?.user?.id) return;

    const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3 MB
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Error",
        description: "Image size must be less than 3 MB",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLocalUploading(true);
      const fileExt = file.name.split('.').pop();
      const filePath = `${session.user.id}/${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', session.user.id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Avatar updated successfully",
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Error",
        description: "Failed to upload avatar",
        variant: "destructive",
      });
    } finally {
      setIsLocalUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 mt-4">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <label 
              htmlFor="avatar-upload" 
              className="cursor-pointer relative group"
              aria-label="Upload avatar"
            >
              <Input 
                type="file" 
                id="avatar-upload" 
                className="hidden" 
                accept="image/*"
                onChange={handleAvatarUpload}
                disabled={isUploading || isLocalUploading}
              />
              <Avatar className="w-12 h-12 transition-opacity group-hover:opacity-80">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt="Profile" />
                ) : (
                  <AvatarFallback className="bg-grace-dark text-white">
                    {session?.user?.email?.[0].toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
              {(isUploading || isLocalUploading) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </label>
          </TooltipTrigger>
          <TooltipContent>
            <p>Click to upload your avatar (max 3 MB)</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <div className="text-sm">
        <p className="font-medium text-app-text-primary">{session?.user?.email}</p>
        <p className="text-app-text-secondary">Signed in</p>
      </div>
    </div>
  );
}